import { EmailDeliveryStatus } from 'api/types';

export type DeliverySummary = 'sent' | 'delivered' | 'failed';

const normalizeRecipient = (recipient: string) => recipient.trim().toLowerCase();

// "Delivered" requires an event for every current recipient: a recipient with
// no matching event counts against delivery, so one delivered event out of
// several cannot claim the whole send arrived.
export const getDeliverySummary = (
  recipients: string[],
  emailDelivery: EmailDeliveryStatus[] | null | undefined
): DeliverySummary => {
  const delivery = emailDelivery ?? [];

  if (delivery.some((entry) => entry.status === 'failed')) {
    return 'failed';
  }

  const deliveredRecipients = new Set(
    delivery
      .filter((entry) => entry.status === 'delivered')
      .map((entry) => normalizeRecipient(entry.recipient))
  );
  const normalizedRecipients = recipients.map(normalizeRecipient).filter(Boolean);
  const isFullyDelivered =
    normalizedRecipients.length > 0 &&
    normalizedRecipients.every((recipient) => deliveredRecipients.has(recipient));

  return isFullyDelivered ? 'delivered' : 'sent';
};

export type DeliverySummaryLocaleKey = 'sent' | 'delivered' | 'deliveryFailed';

const DELIVERY_SUMMARY_LOCALE_KEY: Record<DeliverySummary, DeliverySummaryLocaleKey> = {
  sent: 'sent',
  delivered: 'delivered',
  failed: 'deliveryFailed',
};

export const describeDeliverySummary = (
  summary: DeliverySummary,
  t: (key: DeliverySummaryLocaleKey) => string
): string => t(DELIVERY_SUMMARY_LOCALE_KEY[summary]);

export type DeliveryFailureReasonLocaleKey =
  | 'deliveryFailureReasonSuppressBounce'
  | 'deliveryFailureReasonSuppressComplaint'
  | 'deliveryFailureReasonSuppressUnsubscribe'
  | 'deliveryFailureReasonBounce';

// HR users shouldn't have to decode a provider's reason codes. Anything not
// listed here falls back to the raw string, which is all we have for it.
const KNOWN_FAILURE_REASONS: Record<string, DeliveryFailureReasonLocaleKey> = {
  'suppress-bounce': 'deliveryFailureReasonSuppressBounce',
  'suppress-complaint': 'deliveryFailureReasonSuppressComplaint',
  'suppress-unsubscribe': 'deliveryFailureReasonSuppressUnsubscribe',
  bounce: 'deliveryFailureReasonBounce',
};

// The code itself is left out: support reads it from the email logs, and it
// means nothing to the person reading the sidebar.
export const describeDeliveryFailure = (
  entry: EmailDeliveryStatus,
  t: (key: DeliveryFailureReasonLocaleKey) => string
): string => {
  if (!entry.reason) {
    return entry.recipient;
  }

  const localeKey = KNOWN_FAILURE_REASONS[entry.reason];
  if (!localeKey) {
    return `${entry.recipient} — ${entry.reason}`;
  }

  return `${entry.recipient} — ${t(localeKey)}`;
};
